import { PspRules } from "@CommonTypes/backend/PspRules";
import { PALLET_DEFAULT_MAX_SKUS } from "@CommonUtils/pog/pallet";

type WeightRules = NonNullable<PspRules["weight_rules"]>;

/**
 * The weight rules as a config file carries them: everything except the two
 * fields that are the backend's to set.
 *
 * `report` — owa-backend's `buildWeightRules` hardcodes `report: true` and
 * never reads the value we send — so writing our own into the file would let
 * an import/export round trip silently rewrite whatever the user supplied. It
 * still goes out on the generation payload, where the contract requires it.
 *
 * `heavy_brand_block_level` — the backend derives it per run from the item
 * master and ignores any value sent. A file exported while it was still asked
 * may carry one; it is accepted and dropped, whatever it says.
 */
type WeightRulesInFile = Omit<WeightRules, "report" | "heavy_brand_block_level">;

export type PspRulesFromFile = Omit<PspRules, "weight_rules"> & {
  weight_rules?: WeightRulesInFile;
};

/** What arrives at either boundary: our own rules, or an older file's. */
type PspRulesWithOptionalReport = Omit<PspRules, "weight_rules"> & {
  weight_rules?: WeightRulesInFile & { report?: boolean; heavy_brand_block_level?: unknown };
};

interface PspRulesTemplate {
  _version: "1";
  rules: PspRulesFromFile;
}

const OBJECT_SECTIONS = new Set([
  "fixture",
  "range",
  "merchandising",
  "high_density_mode",
  "high_density_building_blocks_mode",
  "relax_factors",
]);

/**
 * `weight_rules` is absent by design: requiring it would reject configs
 * exported before it existed, and configs from merchants with the gate off.
 * It still round-trips; an import without it falls back to defaults.
 */
const REQUIRED_RULE_SECTIONS = [
  "fixture",
  "range",
  "merchandising",
  "high_density_mode",
  "high_density_building_blocks_mode",
  "mudmap_level",
  "optimize_on",
  "relax_factors",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Absent is fine (see REQUIRED_RULE_SECTIONS comment); present, the section
// must be the whole shape, since a partial one is malformed by contract.
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

function isValidWeightRules(value: unknown): value is WeightRulesInFile {
  if (!isRecord(value)) return false;

  if (
    value.default_shelf_weight_limit !== null &&
    !isFiniteNumber(value.default_shelf_weight_limit)
  )
    return false;

  if (value.heavy_rule !== null) {
    if (!isRecord(value.heavy_rule)) return false;
    if (!isFiniteNumber(value.heavy_rule.item_threshold)) return false;
    if (!isFiniteNumber(value.heavy_rule.max_deck_y)) return false;
  }

  if (typeof value.resolve_overweight_shelves !== "boolean") return false;
  if (typeof value.swap_heavy_brands_below_max_height !== "boolean") return false;
  if (
    !isFiniteNumber(value.swap_fill_floor_pct) ||
    value.swap_fill_floor_pct < 0 ||
    value.swap_fill_floor_pct > 1
  )
    return false;

  return true;
}

// Absent is fine (a config from before pallets, or from base generation);
// present, it is the whole shape the backend reads.
function isValidPalletRules(value: unknown): value is NonNullable<PspRules["pallets"]> {
  if (!isRecord(value)) return false;
  const products = value.max_products_per_pallet;
  if (
    !isFiniteNumber(products) ||
    !Number.isInteger(products) ||
    products < 1 ||
    products > PALLET_DEFAULT_MAX_SKUS
  )
    return false;
  const height = value.max_stack_height;
  return height === null || (isFiniteNumber(height) && height > 0);
}

function isValidPspRulesTemplate(data: unknown): data is PspRulesTemplate {
  if (!isRecord(data)) return false;
  if (data._version !== "1") return false;
  if (!isRecord(data.rules)) return false;
  const rules = data.rules;
  if (
    !REQUIRED_RULE_SECTIONS.every((key) =>
      OBJECT_SECTIONS.has(key) ? isRecord(rules[key]) : typeof rules[key] === "string",
    )
  )
    return false;
  return (
    (rules.weight_rules === undefined || isValidWeightRules(rules.weight_rules)) &&
    (rules.pallets === undefined || isValidPalletRules(rules.pallets))
  );
}

/** Both boundaries go through here, so no config file can carry the backend's fields. */
function withoutReport(rules: PspRulesWithOptionalReport): PspRulesFromFile {
  if (!rules.weight_rules) {
    const { weight_rules: _absent, ...withNoWeightRules } = rules;
    return withNoWeightRules;
  }

  const { report: _report, heavy_brand_block_level: _derived, ...weightRules } = rules.weight_rules;
  return { ...rules, weight_rules: weightRules };
}

export function exportPspRulesConfig(rules: PspRules): string {
  const template: PspRulesTemplate = { _version: "1", rules: withoutReport(rules) };
  return JSON.stringify(template, null, 2);
}

export async function parsePspRulesTemplate(file: File): Promise<PspRulesFromFile> {
  const text = await file.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("File is not valid JSON");
  }
  if (!isValidPspRulesTemplate(parsed)) {
    throw new Error("File does not contain a valid planogram configuration");
  }
  return withoutReport(parsed.rules);
}
